import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Dropdown, Input, List, Tag } from 'tea-component';
import './index.scss';
import useLanguage from '@op/hooks/useLanguage';

export default function EmailInput({
  inputProps,
  className,
  placeholder,
  value: defaultTagList = [],
  onChange,
}: {
  inputProps?: typeof Input;
  className?: string;
  placeholder: string;
  value: Array<string>;
  onChange: (value: Array<string>, isError: boolean) => void;
}) {
  // /^([A-Za-z0-9_\-\.])+\@([A-Za-z0-9_\-\.])+\.([A-Za-z]{2,4})$/.test("2921855939@qq.com")
  // console.log(/([a-zA-Z0-9])+@([a-zA-Z0-9])+\.([0-9a-zA-Z]+)$/.test("2921855939@qq.com"));
  const [value, setValue] = useState('');
  const [spanValue, setSpanValue] = useState('');
  const [tagList, setTagList] = useState<Array<string>>(defaultTagList);
  const { formatMessage } = useLanguage();

  const deleteTag = (value: string) => {
    const list = tagList.filter((item) => item !== value);
    setTagList([...list]);
  };

  const addTag = (value: string, callback?: () => void) => {
    if (value) {
      setTagList([...tagList, value]);
      callback?.();
      setValue('');
      setSpanValue('');
    }
  };

  const inputBoxRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const spanRef = useRef<HTMLSpanElement | null>(null);
  const [inputStyle, setInputStyle] = useState({});

  const setInputWidth = () => {
    let { clientWidth, scrollWidth } = spanRef.current || {};
    let { clientWidth: boxClientWidth } = inputBoxRef.current || {};
    let width = Math.max(clientWidth || 0, scrollWidth || 0) + 20;

    width = Math.min(width, boxClientWidth || width);
    setInputStyle({ width });
  };

  const renderTagList = useMemo(() => {
    const regex = /^([A-Za-z0-9_\-\.])+\@([A-Za-z0-9_\-\.])+\.([A-Za-z]{2,4})$/;
    return tagList.map((item) => {
      return { text: item, isError: !regex.test(item) };
    });
  }, [tagList]);

  const placeholderValue = useMemo(() => {
    return tagList.length > 0 || value ? '' : placeholder;
  }, [tagList, value, formatMessage]);

  useEffect(() => {
    setSpanValue(value || placeholderValue);
  }, [value, placeholderValue]);

  useEffect(() => {
    setInputWidth();
  }, [spanValue]);

  useEffect(() => {
    onChange(
      tagList,
      renderTagList.some((item) => item.isError),
    );
  }, [renderTagList]);

  useEffect(() => {
    setTagList(defaultTagList);
  }, [defaultTagList]);

  return (
    <Dropdown
      clickClose={false}
      appearance="pure"
      style={{ marginRight: 10 }}
      matchButtonWidth
      open={!!value}
      className="dbo-email-input-box-wrapper"
      onFocus={() => inputRef.current?.focus()}
      button={
        <div
          className="dbo-email-input-wrapper"
          ref={(ref) => (inputBoxRef.current = ref)}
        >
          {renderTagList.map((item) => (
            <Tag
              key={item.text}
              className="dbo-tag-item"
              theme={item.isError ? 'error' : 'default'}
              dark={item.isError}
              onClose={() => deleteTag(item.text)}
            >
              {item.text}
            </Tag>
          ))}
          <Input
            {...(inputProps || {})}
            className={`${className} dbo-email-input`}
            value={value}
            style={inputStyle}
            ref={(ref) => (inputRef.current = ref)}
            placeholder={placeholderValue}
            onPressEnter={() => addTag(value)}
            onChange={(newValue) => {
              const index = newValue.indexOf(';');
              const lIndex = newValue.indexOf('；');
              if (index !== -1 || lIndex !== -1) {
                let rIndex = index !== -1 ? index : lIndex;
                addTag(newValue.substring(0, rIndex));
              } else {
                setValue(newValue);
              }
            }}
            onInput={() => {
              setSpanValue(inputRef.current?.value || '');
            }}
            onKeyDown={(event) => {
              if (event.code === 'Backspace' && !spanValue) {
                let len = tagList.length;
                len > 0 && deleteTag(tagList[len - 1]);
              }
            }}
          />
          <span
            ref={(ref) => (spanRef.current = ref)}
            className="input-width-span"
          >
            {spanValue || placeholderValue}
          </span>
        </div>
      }
    >
      {(close) => (
        <List type="option">
          <List.Item onClick={() => addTag(value, close)}>{value}</List.Item>
        </List>
      )}
    </Dropdown>
  );
}
